//dashboard/src/components/operationsAssociate/ViewAssetModal.jsx

import React from "react";
import { FaHistory, FaClock } from "react-icons/fa";

const Section = ({ title, children, icon: Icon }) => {
    return (
        <div className="border border-gray-200 rounded-lg mb-6 shadow-sm bg-white">
            <h3 className="w-full text-left px-5 py-3 bg-gray-50 border-b border-gray-200 font-semibold text-gray-800 flex items-center rounded-t-lg">
                {Icon && <Icon className="mr-2 text-orange-500" />}
                {title}
            </h3>
            <div className="p-5">{children}</div>
        </div>
    );
};

const ReadOnlyField = ({ label, value }) => (
    <div>
        <label className="block mb-1 text-xs font-bold text-gray-500 uppercase tracking-wider">{label}</label>
        <div className="w-full border border-gray-200 rounded-lg px-4 py-2.5 bg-gray-50 text-gray-800 font-medium">
            {value || "N/A"}
        </div>
    </div>
);

export default function ViewAssetModal({ asset, closeModal }) {
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleString(undefined, { 
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
        });
    };

    const getHistoryStatusColor = (status) => {
        if (status === "Active") return "text-green-600 bg-green-50";
        if (status === "Pending Acceptance") return "text-orange-600 bg-orange-50";
        if (status === "Rejected") return "text-red-600 bg-red-50";
        return "text-gray-600 bg-gray-100";
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-0 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-white">
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                        <span className="p-2 bg-orange-100 rounded-lg text-orange-600 mr-3"></span>
                        Asset Details
                    </h2>
                    <button type="button" onClick={closeModal} className="text-gray-400 hover:text-red-500 p-2 hover:bg-red-50 rounded-full transition-all">
                        &times;
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="overflow-y-auto p-6 space-y-6">
                    
                    <Section title="General Information" icon={FaHistory}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            <ReadOnlyField label="Asset Name" value={asset.name} />
                            <ReadOnlyField label="Category" value={asset.category} />
                            <ReadOnlyField label="Serial Number" value={asset.serialNumber} />
                            
                            <div className="sm:col-span-2 lg:col-span-2">
                                <label className="block mb-1 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</label>
                                <span className={`px-3 py-1 rounded-full text-sm font-bold inline-block ${getHistoryStatusColor(asset.status)}`}>
                                    {asset.status}
                                </span>
                            </div>
                            <ReadOnlyField label="Condition" value={asset.condition} />
                            <ReadOnlyField label="Location" value={asset.location} />
                        </div>
                    </Section>

                    <Section title="Audit Trail & History" icon={FaHistory}>
                        {asset.history && asset.history.length > 0 ? (
                            <div className="space-y-0 relative">
                                {/* Vertical Line */}
                                <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gray-200"></div>
                                
                                {asset.history.slice().reverse().map((log, index) => (
                                    <div key={index} className="relative pl-10 pb-6 last:pb-0">
                                        {/* Dot */}
                                        <div className={`absolute left-[11px] top-1 w-2.5 h-2.5 rounded-full border-2 border-white shadow-sm ${log.status === 'Active' ? 'bg-green-500' : log.status === 'Rejected' ? 'bg-red-500' : 'bg-orange-400'}`}></div>
                                        
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1">
                                            <span className="font-bold text-gray-800 text-sm flex items-center">
                                                {log.action}
                                                <span className={`ml-2 px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wide ${getHistoryStatusColor(log.status)}`}>
                                                    {log.status}
                                                </span>
                                            </span>
                                            <span className="text-xs text-gray-400 flex items-center mt-1 sm:mt-0">
                                                <FaClock className="mr-1" size={10} />
                                                {formatDate(log.timestamp)}
                                            </span>
                                        </div>
                                        
                                        <div className="text-sm text-gray-600 mb-1">
                                            <span className="font-medium text-gray-500">By:</span> {log.actor ? `${log.actor.firstName} ${log.actor.lastName}` : 'System'}
                                        </div>

                                        {log.details && (
                                            <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-100 text-sm text-gray-700 italic">
                                                "{log.details}"
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-4 text-gray-400 text-sm">No history recorded for this asset.</div>
                        )}
                    </Section>

                    {asset.notes && (
                        <Section title="Additional Notes">
                            <div className="text-gray-700 text-sm bg-orange-50 p-4 rounded-lg border border-orange-100">
                                {asset.notes}
                            </div>
                        </Section>
                    )}
                </div>

                <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                    <button type="button" onClick={closeModal} className="px-6 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}